import React, { useEffect, useState, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import map from 'lodash/map';
import isEmpty from 'lodash/isEmpty';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableContainer from '@material-ui/core/TableContainer';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import Paper from '@material-ui/core/Paper';
import Menu from '@material-ui/core/Menu';
import MenuItem from '@material-ui/core/MenuItem';
import Divider from '@material-ui/core/Divider';
import IconButton from '@material-ui/core/IconButton';
import MoreVert from '@material-ui/icons/MoreVert';
import LinearProgress from '@material-ui/core/LinearProgress';
import Icon from '@material-ui/core/Icon';
import DeleteIcon from '@material-ui/icons/DeleteOutlineOutlined';
import EditOutlined from '@material-ui/icons/EditOutlined';
import { PageIndex } from 'components/UI/PageIndex';
import { Search } from 'components/Search/Search';
import { UploadDocument } from 'features/Documents/UploadDocument';
import { DocLinkType, documentsPrefix } from 'routes/routes';
import { AlertDialog } from 'components/UI/Dialog/AlertDialog';
import { Row } from 'components/UI/Row';
import { RenameForm } from './components/RenameForm';
import {
  getDocumentsList,
  resetDocuments,
  fetchDocuments,
  getUploadProgress,
  deleteDocument,
  getDocumentsPagination,
  getDocumentsSearchTerm
} from './documents';

const AllDocumentsList = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const history = useHistory();
  const documents = useSelector(getDocumentsList);
  const pagination = useSelector(getDocumentsPagination);
  const searchTerm = useSelector(getDocumentsSearchTerm);
  const { file, progress } = useSelector(getUploadProgress);
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const [currentDocument, setCurrentDocument] = useState<string | null>(null);
  const [openDeleteDialog, setOpenDeleteDialog] = useState<boolean>(false);
  const [showInput, setShowInput] = useState<any>({});
  const tableRef = useRef<HTMLTableElement>(null);
  useEffect(() => {
    return () => {
      dispatch(resetDocuments());
    };
  }, [dispatch]);

  useEffect(() => {
    dispatch(fetchDocuments({ pagination: { index: 0, size: 10 } }));
  }, [dispatch]);

  return (
    <>
      <Search
        resetFunction={() => dispatch(fetchDocuments({ pagination: { index: 0, size: 10 } }))}
        searchFunction={(search: string) => dispatch(fetchDocuments({ pagination: { index: 0, size: 10 }, search }))}
        searchLabel="document"
      />
      <div style={{ height: '20px', borderBottom: 'solid 1px lightgrey' }} />
      <div style={{ height: '20px' }} />
      <UploadDocument showDropArea />
      {progress > 0 && (
        <div style={{ width: '100%', display: 'flex', flexDirection: 'column' }}>
          <span>
            {t('documents.uploading')} {file}
          </span>
          <div style={{ height: '10px' }} />
          <div style={{ width: '100%' }}>
            <LinearProgress variant="determinate" value={progress} />
          </div>
        </div>
      )}

      <TableContainer component={Paper} style={{ marginTop: '30px', maxHeight: '400px' }} elevation={3}>
        <Table stickyHeader aria-label="order list" ref={tableRef}>
          <TableHead>
            <TableRow>
              <TableCell>{t('documents.title')}</TableCell>
              <TableCell>{t('documents.size')}</TableCell>
              <TableCell style={{ width: '50px' }} />
            </TableRow>
          </TableHead>
          <TableBody>
            {isEmpty(documents) && (
              <>
                <TableRow>
                  <TableCell>{t('documents.no doc found')}</TableCell>
                </TableRow>
              </>
            )}
            {map(documents, d => (
              <TableRow key={`doc_${d.id}`}>
                <TableCell colSpan={showInput[d.id] ? 2 : 1}>
                  {showInput[d.id] ? (
                    <RenameForm
                      name={d.name}
                      closeEdit={() => {
                        setShowInput({
                          ...showInput,
                          [d.id]: false
                        });
                      }}
                      id={d.id}
                    />
                  ) : (
                    <>{d.name}</>
                  )}
                </TableCell>
                <TableCell colSpan={showInput[d.id] ? 2 : 1}>
                  {formatBytes(d.size)}
                </TableCell>
                {!showInput[d.id] && (
                  <TableCell style={{ width: '50px' }} align="center">
                    <IconButton
                      aria-controls="documents-menu"
                      aria-haspopup="true"
                      onClick={e => {
                        setCurrentDocument(d.id);
                        setMenuAnchor(e.currentTarget);
                      }}
                    >
                      <MoreVert />
                    </IconButton>
                  </TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <PageIndex
        margin="25px 0"
        justifyContent="center"
        index={pagination.index}
        indexesCount={pagination.indexesCount || 0}
        onChange={async (_, index: number) => {
          await dispatch(fetchDocuments({ pagination: { index }, search: searchTerm }));
          if (tableRef) {
            tableRef?.current?.scrollIntoView();
            window.scrollTo({ top: 0 });
          }
        }}
      />
      <Menu
        elevation={1}
        id="serial-numbers-menu"
        anchorEl={menuAnchor}
        keepMounted
        open={Boolean(menuAnchor)}
        onClose={() => {
          setMenuAnchor(null);
          setCurrentDocument(null);
        }}
      >
        <MenuItem
          onClick={() => {
            history.push(`${documentsPrefix}/link/${currentDocument}/${DocLinkType.PRODUCT}`);
            setMenuAnchor(null);
            setCurrentDocument(null);
          }}
        >
          {t('documentsLink.link products')}
        </MenuItem>
        <MenuItem
          onClick={() => {
            history.push(`${documentsPrefix}/link/${currentDocument}/${DocLinkType.SERIAL_NUMBER}`);
            setMenuAnchor(null);
            setCurrentDocument(null);
          }}
        >
          {t('documentsLink.link serial numbers')}
        </MenuItem>
        <MenuItem
          onClick={() => {
            history.push(`${documentsPrefix}/link/${currentDocument}/${DocLinkType.ORDER_LIST}`);
            setMenuAnchor(null);
            setCurrentDocument(null);
          }}
        >
          {t('documentsLink.link order list')}
        </MenuItem>
        <Divider />
        <MenuItem
          onClick={() => {
            if (currentDocument) {
              setShowInput({
                ...showInput,
                [currentDocument]: true
              });
            }
            setMenuAnchor(null);
          }}
        >
          <Row alignItems="center" justifyContent="space-between">
            <span style={{ marginTop: '5px' }}>{t('label.rename')}</span>
            <Icon color="primary">
              <EditOutlined />
            </Icon>
          </Row>
        </MenuItem>
        <MenuItem
          onClick={() => {
            setOpenDeleteDialog(true);
            setMenuAnchor(null);
          }}
        >
          <Row alignItems="center" justifyContent="space-between">
            <span style={{ marginTop: '5px' }}>{t('label.delete')}</span>
            <Icon color="secondary">
              <DeleteIcon />
            </Icon>
          </Row>
        </MenuItem>
      </Menu>
      {/* <RenameDialog
        handleAccept={() => {
          setCurrentDocument(null);
          setOpenRenameDialog(false);
        }}
        handleCancel={() => {
          setCurrentDocument(null);
          setOpenRenameDialog(false);
        }}
        id={currentDocument}
        open={openRenameDialog && !isEmpty(currentDocument)}
      /> */}
      <AlertDialog
        handleAccept={() => {
          if (currentDocument) {
            dispatch(deleteDocument(currentDocument));
          }
          setCurrentDocument(null);
          setOpenDeleteDialog(false);
        }}
        handleCancel={() => {
          setCurrentDocument(null);
          setOpenDeleteDialog(false);
        }}
        open={openDeleteDialog && !isEmpty(currentDocument)}
        title={t('documents.delete confirm title')}
        text={t('documents.delete confirm text')}
      />
    </>
  );
};

const formatBytes = (bytes: number, decimals: number = 2) => {
  if (bytes === 0) {
    return '0 b'
  };
  
  const k = 1024;
  const dm = decimals < 0 ? 0 : decimals;
  const sizes = ['b', 'kb', 'mb', 'gb', 'tb', 'pb', 'eb', 'zb', 'yb'];

  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
};

export { AllDocumentsList };
